// What the printed order sheet adds under the order, by where the order stands:
//
//   not sent yet   the sheet goes into the parcel, for the buyer: how to send
//                  items back from their account (no bank details: the money
//                  goes back the way it was paid), the period, the address,
//                  how to reach the seller (ReturnGuide)
//   sent           the sheet is for whoever works on the order next: where it
//                  stands — the parcel, the courier, delivery, what happens
//                  next — and everything that happened to it (StatusReport)
//
// Both are print-only: on screen the tabs say the same.
import React from "react"
import OrderTimeline from "./OrderTimeline"
import { dateTime, dateOnly, orderState } from "./ui"
import { PARCEL_STATES } from "./Tracking"

const Heading = ({ children }) => <div className="text-[11px] uppercase tracking-wide text-black/50 mb-2">{children}</div>

/**
 * For the buyer, in the parcel: how to send items back. `renderQR(url)` (the
 * project's) draws the code that opens the order in their account; without
 * it the link is printed as text.
 */
export function ReturnGuide({ order, t, locale, renderQR }) {
  const days = order?.returned?.window?.days || 14
  const url = order?.buyerOrderUrl || ""
  const address = order?.returned?.returnAddress || ""
  const seller = order?.seller || {}
  const id = order?.orderId || order?._id
  const qr = url && renderQR ? renderQR(url) : null
  return (
    <div className="mt-5 pt-4 border-t border-black/10 text-sm" data-testid="print-return-guide">
      <div className="text-lg font-bold mb-1">{t`Returning items`}</div>
      <p className="m-0 mb-3 text-black/70">{t("You can return items within %s days of receiving them, without giving a reason. Everything is done from your account on the site.", days)}</p>
      {/* the order in the buyer's account, one scan away: managing it starts there */}
      {url && (
        <div className="flex items-center gap-4 mb-3 rounded-md border border-black/10 p-3 break-inside-avoid" data-testid="print-order-link">
          {qr && <div className="shrink-0 bg-white p-1" data-testid="print-order-qr">{qr}</div>}
          <div>
            <div className="font-semibold">{qr ? t`To manage your order, scan the code` : t`To manage your order, open:`}</div>
            {!qr && <div className="[overflow-wrap:anywhere]">{url}</div>}
            <div className="text-black/60">{t("It opens order %s in your account: sign in with %s.", id, order?.email || "")}</div>
          </div>
        </div>
      )}
      <ol className="m-0 pl-5 list-decimal flex flex-col gap-1.5">
        <li>{t`Open the order, choose the “Request for return” tab, pick the items and the reason, and send the request.`}</li>
        <li>{t`The seller answers by email, and in your order, with where to send the items.`}</li>
        <li>{t("Pack the items, write the order number %s on the parcel or put this sheet inside, and send it.", id)}</li>
        <li>{t`Once the seller receives the items, the money goes back the way you paid. You are told by email.`}</li>
      </ol>
      {/* where to send it and whom to ask, side by side */}
      <div className="mt-4 grid grid-cols-2 gap-6 break-inside-avoid">
        {address && (
          <div>
            <Heading>{t`Return address`}</Heading>
            <div className="whitespace-pre-line">{address}</div>
          </div>
        )}
        {(seller.name || seller.email || seller.phone) && (
          <div>
            <Heading>{t`Questions about your order`}</Heading>
            {[seller.name, seller.email, seller.phone].filter(Boolean).map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const Row = ({ label, children }) =>
  children ? (
    <tr className="align-top">
      <td className="py-1 pr-4 w-[200px] text-black/60">{label}</td>
      <td className="py-1">{children}</td>
    </tr>
  ) : null

/** For whoever works on the order next: where it stands, and its history. */
export function StatusReport({ order, t, locale }) {
  const state = orderState(order)
  const tracking = order?.tracking
  const parcel = tracking?.state ? PARCEL_STATES[tracking.state] : null
  const shippedAt = order?.metadata?.shippedAt
  const delivery = order?.delivery
  const requests = order?.returned?.requests || []
  const openRequests = requests.filter((request) => request.status === "open").length
  const awaited = requests.filter((request) => request.status === "accepted" && !request.returnedAt).length
  const openCancel = (order?.cancelRequests || []).some((request) => request.status === "open")
  return (
    <div className="mt-8 pt-6 border-t border-black/10 text-sm" data-testid="print-status-report">
      <div className="text-lg font-bold mb-1">{t`Where the order stands`}</div>
      <p className="m-0 mb-3 text-black/60">{t("Printed %s", dateTime(new Date(), locale))}</p>
      <table className="w-full text-sm break-inside-avoid">
        <tbody>
          <Row label={t`Status`}>
            <span className="inline-block first-letter:uppercase font-semibold">{t(state.label)}</span>
          </Row>
          <Row label={t`Shipped`}>{shippedAt ? dateTime(shippedAt, locale) : null}</Row>
          <Row label={t`Tracking`}>
            {tracking?.number ? (
              <>
                <span className="font-semibold">{tracking.number}</span>
                {tracking.url ? <span className="text-black/60 break-all"> · {tracking.url}</span> : null}
              </>
            ) : null}
          </Row>
          <Row label={t`Courier status`}>
            {parcel ? (
              <>
                {t(parcel.label)}
                {tracking.text ? ` · ${tracking.text}` : ""}
                {tracking.at ? ` · ${dateTime(tracking.at, locale)}` : ""}
              </>
            ) : null}
          </Row>
          <Row label={t`Delivered`}>{order?.deliveredAt ? dateOnly(order.deliveredAt, locale) : null}</Row>
          <Row label={t`Completes automatically`}>{delivery?.completesAt && !delivery?.blocker ? dateOnly(delivery.completesAt, locale) : null}</Row>
          <Row label={t`Waiting for you`}>
            {[
              openCancel && t`A cancellation request to answer`,
              openRequests > 0 && t("%s return request(s) to answer", openRequests),
              awaited > 0 && t("%s accepted return(s) on their way back", awaited),
            ]
              .filter(Boolean)
              .join(" · ") || null}
          </Row>
        </tbody>
      </table>
      <div className="mt-6">
        <Heading>{t`History`}</Heading>
        <OrderTimeline events={order?.history || []} currency={order?.currency} locale={locale} t={t} />
      </div>
    </div>
  )
}

/** The section the print adds for this order: the buyer's guide before it is sent, the report after. */
export default function PrintSections({ order, t, locale, shipsSomething, renderQR }) {
  if (!order) return null
  const notSent = order.orderStatus === "paid" && shipsSomething
  return <div className="hidden print:block">{notSent ? <ReturnGuide order={order} t={t} locale={locale} renderQR={renderQR} /> : <StatusReport order={order} t={t} locale={locale} />}</div>
}
